"use client";

import { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";

type IconButtonSize = "sm" | "md" | "lg";
type IconButtonVariant = "default" | "ghost" | "danger";

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: IconButtonSize;
  variant?: IconButtonVariant;
  icon: ReactNode;
  label: string;
}

const sizeStyles: Record<IconButtonSize, string> = {
  sm: "w-7 h-7 [&>svg]:w-3.5 [&>svg]:h-3.5",
  md: "w-9 h-9 [&>svg]:w-4 [&>svg]:h-4",
  lg: "w-11 h-11 [&>svg]:w-5 [&>svg]:h-5",
};

const variantStyles: Record<IconButtonVariant, string> = {
  default:
    "bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] hover:border-[var(--color-text-muted)]",
  ghost:
    "bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text-primary)]",
  danger: "bg-transparent text-red-600 hover:bg-red-500/10 hover:text-red-700",
};

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    { size = "md", variant = "default", icon, label, className = "", ...props },
    ref
  ) {
    return (
      <button
        ref={ref}
        aria-label={label}
        title={label}
        className={`inline-flex items-center justify-center rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-text-muted)] disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 ${sizeStyles[size]} ${variantStyles[variant]} ${className}`}
        {...props}
      >
        {icon}
      </button>
    );
  }
);

export { IconButton, type IconButtonProps };
